import { Search, LayoutGrid, Rows, Flame, TrendingUp, DollarSign, Shield, Clock, ArrowDownAZ, X } from 'lucide-react'
import { Input, Select, Button } from '@/components/ui'
import { ENTITIES, STATUSES, CATEGORIES, cn } from '@/lib/utils'
import { useUIStore, type SortMode } from '@/stores/uiStore'

const sortModes: { mode: SortMode; label: string; icon: React.ComponentType<{ size?: number }> }[] = [
  { mode: 'heat', label: 'Hottest', icon: Flame },
  { mode: 'trend', label: 'Trending', icon: TrendingUp },
  { mode: 'value', label: 'Most Valuable', icon: DollarSign },
  { mode: 'defensibility', label: 'Most Defensible', icon: Shield },
  { mode: 'recent', label: 'Recent', icon: Clock },
  { mode: 'name', label: 'A→Z', icon: ArrowDownAZ },
]

export function Filters() {
  const {
    search, filterEntity, filterStatus, filterCategory, sortMode, view,
    setSearch, setFilterEntity, setFilterStatus, setFilterCategory, setSortMode, setView, reset,
  } = useUIStore()

  const hasFilters = search || filterEntity !== 'all' || filterStatus !== 'all' || filterCategory !== 'all'

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/40" />
          <Input
            placeholder="Search apps, descriptions, stacks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)}>
          <option value="all">All entities</option>
          {ENTITIES.map((e) => <option key={e} value={e}>{e}</option>)}
        </Select>

        <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>

        <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>

        <div className="flex items-center gap-1 border border-cream/15 rounded-md p-0.5 bg-navy/30">
          <button
            onClick={() => setView('grid')}
            className={cn('p-1.5 rounded transition-colors', view === 'grid' ? 'bg-gold/15 text-gold-soft' : 'text-cream/55 hover:text-cream')}
            title="Grid view"
          >
            <LayoutGrid size={14} />
          </button>
          <button
            onClick={() => setView('table')}
            className={cn('p-1.5 rounded transition-colors', view === 'table' ? 'bg-gold/15 text-gold-soft' : 'text-cream/55 hover:text-cream')}
            title="Table view"
          >
            <Rows size={14} />
          </button>
        </div>

        {hasFilters && (
          <Button size="sm" variant="quiet" onClick={reset}>
            <X size={12} /> Clear
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="eyebrow">Sort by</span>
        {sortModes.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            onClick={() => setSortMode(mode)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-mono uppercase tracking-[0.1em] transition-all',
              sortMode === mode
                ? 'bg-gold/15 text-gold-soft border-gold/40'
                : 'bg-navy/30 text-cream/60 border-cream/10 hover:text-cream hover:border-cream/25',
            )}
          >
            <Icon size={11} />
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}

export function applyFilters(apps: import('@/lib/types').AppRecord[]): import('@/lib/types').AppRecord[] {
  const { search, filterEntity, filterStatus, filterCategory, sortMode } = useUIStore.getState()
  const q = search.toLowerCase().trim()

  const result = apps.filter((a) => {
    if (filterEntity !== 'all' && a.entity !== filterEntity) return false
    if (filterStatus !== 'all' && a.status !== filterStatus) return false
    if (filterCategory !== 'all' && a.category !== filterCategory) return false
    if (q) {
      const hay = [a.name, a.slug, a.description, ...(a.stack ?? []), ...(a.key_features ?? [])]
        .join(' ').toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })

  switch (sortMode) {
    case 'heat':          result.sort((a, b) => (b.score?.heat ?? 0) - (a.score?.heat ?? 0)); break
    case 'trend':         result.sort((a, b) => (b.score?.trend ?? 0) - (a.score?.trend ?? 0)); break
    case 'value':         result.sort((a, b) => (b.score?.value ?? 0) - (a.score?.value ?? 0)); break
    case 'defensibility': result.sort((a, b) => (b.score?.defensibility ?? 0) - (a.score?.defensibility ?? 0)); break
    case 'recent':        result.sort((a, b) => (b.last_modified ?? '').localeCompare(a.last_modified ?? '')); break
    case 'name':          result.sort((a, b) => a.name.localeCompare(b.name)); break
  }
  return result
}
